// src/pages/parametres/sauvegarde/SauvegardeManuelle.jsx
import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Spinner, Button, ProgressBar, Table, Badge, Modal } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import toast, { Toaster } from 'react-hot-toast';
import Header from '../../layouts/Header';
import Footer from '../../layouts/Footer';
import { COLORS } from '../../constants/colors';

const showToast = (message, type = 'success') => {
  const toastOptions = {
    duration: 3000,
    position: 'top-right',
    style: {
      borderRadius: '10px',
      background: '#fff',
      color: '#333',
      boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
      padding: '16px',
      fontSize: '14px',
    },
  };

  switch(type) {
    case 'success':
      toast.success(message, toastOptions);
      break;
    case 'error':
      toast.error(message, toastOptions);
      break;
    case 'warning':
      toast(message, {
        ...toastOptions,
        icon: '⚠️',
        style: { ...toastOptions.style, borderLeft: `4px solid ${COLORS.warning}` }
      });
      break;
    default:
      toast(message, toastOptions);
  }
};

const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const formatDate = (dateString) => {
  if (!dateString) return '-';
  return format(new Date(dateString), 'dd MMMM yyyy à HH:mm', { locale: fr });
};

export default function SauvegardeManuelle() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [saveProgress, setSaveProgress] = useState('');
  const [backups, setBackups] = useState([]);
  const [stats, setStats] = useState({
    totalSize: 0,
    lastBackup: null,
    totalBackups: 0
  });

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [backupToDelete, setBackupToDelete] = useState(null);

  useEffect(() => {
    fetchBackups();
  }, []);

  const fetchBackups = async () => {
    setLoading(true);
    try {
      const userStr = localStorage.getItem('user');
      if (!userStr) {
        navigate('/pages/signin2');
        return;
      }

      const user = JSON.parse(userStr);
      const userId = user.id || user.id_utilisateur;
      
      const response = await fetch(`https://josephsbusiness.com/api/backups.php?user_id=${userId}&action=list`, {
        headers: { 'Accept': 'application/json' }
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }
      
      const result = await response.json();
      
      if (result.success) {
        setBackups(result.backups || []);
        setStats({
          totalSize: result.totalSize || 0,
          lastBackup: result.lastBackup || null,
          totalBackups: result.totalBackups || 0
        });
      } else {
        showToast(result.message || 'Erreur de chargement', 'error');
      }
    } catch (err) {
      console.error('Erreur:', err);
      showToast(err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSauvegarde = async () => {
    setSaving(true);
    setProgress(0);
    setSaveProgress('Préparation de la sauvegarde...');
    
    try {
      const userStr = localStorage.getItem('user');
      const user = JSON.parse(userStr);
      const userId = user.id || user.id_utilisateur;
      
      const progressInterval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 500);

      setSaveProgress('Compression des données...');
      
      const response = await fetch(`https://josephsbusiness.com/api/backups.php?user_id=${userId}&action=create`, {
        method: 'POST',
        headers: { 
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          type: 'manuel',
          tables: ['membres', 'eglises', 'abonnements', 'assistances', 'mariages', 'baptemes', 'naissances', 'deces', 'entrees', 'depenses', 'recommandations']
        })
      });

      clearInterval(progressInterval);
      
      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }
      
      const result = await response.json();
      
      if (result.success) {
        setProgress(100);
        setSaveProgress('Sauvegarde terminée !');
        showToast('Sauvegarde effectuée avec succès', 'success');
        
        setTimeout(() => {
          setProgress(0);
          setSaveProgress('');
          fetchBackups();
        }, 1500);
      } else {
        setProgress(0);
        setSaveProgress('');
        showToast(result.message || 'Erreur lors de la sauvegarde', 'error');
      }
    } catch (err) {
      console.error('Erreur:', err);
      setProgress(0);
      setSaveProgress('');
      showToast('Erreur: ' + err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = async (filename) => {
    try {
      const userStr = localStorage.getItem('user');
      const user = JSON.parse(userStr);
      const userId = user.id || user.id_utilisateur;
      
      const response = await fetch(`https://josephsbusiness.com/api/backups.php?user_id=${userId}&action=download&file=${filename}`, {
        headers: { 'Accept': 'application/json' }
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      showToast('Téléchargement démarré', 'success');
    } catch (err) {
      console.error('Erreur:', err);
      showToast('Erreur lors du téléchargement', 'error');
    }
  };

  const handleDeleteClick = (backup) => {
    setBackupToDelete(backup);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!backupToDelete) return;
    
    setDeleting(true);
    try {
      const userStr = localStorage.getItem('user');
      const user = JSON.parse(userStr);
      const userId = user.id || user.id_utilisateur;
      
      const url = `https://josephsbusiness.com/api/backups.php?user_id=${userId}&action=delete&file=${encodeURIComponent(backupToDelete.filename)}`;
      
      const response = await fetch(url, {
        method: 'DELETE',
        headers: { 
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      const responseText = await response.text();
      
      if (!response.ok) {
        try {
          const errorData = JSON.parse(responseText);
          throw new Error(errorData.message || `Erreur HTTP: ${response.status}`);
        } catch (e) {
          throw new Error(`Erreur HTTP: ${response.status}`);
        }
      }
      
      let result;
      try {
        result = JSON.parse(responseText);
      } catch (e) {
        throw new Error('Réponse du serveur invalide');
      }
      
      if (result.success) {
        setBackups(prevBackups => {
          const newBackups = prevBackups.filter(b => b.filename !== backupToDelete.filename);
          
          const newTotalSize = newBackups.reduce((acc, b) => acc + b.size, 0);
          const newLastBackup = newBackups.length > 0 ? newBackups[0].date : null;
          
          setStats({
            totalSize: newTotalSize,
            lastBackup: newLastBackup,
            totalBackups: newBackups.length
          });
          
          return newBackups;
        });
        
        setShowDeleteModal(false);
        setBackupToDelete(null);
        showToast('Sauvegarde supprimée avec succès', 'success');
      } else {
        showToast(result.message || 'Erreur lors de la suppression', 'error');
        fetchBackups();
      }
    } catch (err) {
      console.error('❌ Erreur détaillée:', err);
      showToast('Erreur: ' + err.message, 'error');
      fetchBackups();
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <>
        <Header />
        <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
            <div className="text-center">
              <Spinner animation="border" variant="primary" style={{ width: '4rem', height: '4rem' }} />
              <h5 className="fw-bold mt-3" style={{ color: COLORS.primaryDark }}>Chargement des sauvegardes</h5>
              <p className="text-muted">Récupération des données en cours...</p>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <Toaster />
      <div className="main main-app p-3 p-lg-4" style={{ background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)' }}>
        <div className="container-fluid px-0 px-lg-3">

          {/* ===== EN‑TÊTE MODERNE ===== */}
          <div className="header-modern mb-4 fade-in">
            <div className="header-left">
              <div className="header-icon-wrapper" style={{ background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.indigo})` }}>
                <i className="ri-database-2-line"></i>
              </div>
              <div>
                <h5 className="gradient-text">Sauvegarde manuelle</h5>
                <div className="d-flex flex-wrap gap-2">
                  <span className="badge-modern">
                    <i className="ri-database-2-line"></i> {stats.totalBackups} sauvegarde(s)
                  </span>
                  <span className="badge-modern" style={{ background: `${COLORS.success}20`, color: COLORS.success }}>
                    <i className="ri-time-line"></i> Dernière : {stats.lastBackup ? formatDate(stats.lastBackup) : 'Jamais'}
                  </span>
                  <span className="badge-modern" style={{ background: `${COLORS.info}20`, color: COLORS.info }}>
                    <i className="ri-hard-drive-line"></i> {formatFileSize(stats.totalSize)}
                  </span>
                </div>
              </div>
            </div>
            <div className="d-flex gap-2 flex-wrap">
              <button className="btn-modern-outline" onClick={fetchBackups} disabled={loading}>
                <i className={`ri-refresh-line ${loading ? 'fa-spin' : ''}`}></i> Actualiser
              </button>
              <button
                className="btn-modern-primary"
                onClick={handleSauvegarde}
                disabled={saving}
                style={{
                  background: `linear-gradient(135deg, ${COLORS.success}, ${COLORS.emerald})`,
                  border: 'none',
                  padding: '0.4rem 1.2rem',
                  borderRadius: '4px',
                  fontSize: '0.85rem',
                  fontWeight: '500',
                  color: 'white',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  transition: 'all 0.2s',
                  boxShadow: `0 4px 10px ${COLORS.success}40`
                }}
              >
                {saving ? (
                  <><Spinner size="sm" className="me-1" /> Sauvegarde...</>
                ) : (
                  <><i className="ri-save-line me-1"></i> Lancer la sauvegarde</>
                )}
              </button>
            </div>
          </div>

          {/* ===== BARRE DE PROGRESSION ===== */}
          {saving && (
            <Row className="mb-4 fade-in">
              <Col md={12}>
                <Card className="progress-card-modern">
                  <Card.Body className="p-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="fw-semibold" style={{ color: COLORS.primaryDark }}>{saveProgress}</span>
                      <span className="fw-bold" style={{ color: COLORS.primary }}>{progress}%</span>
                    </div>
                    <ProgressBar
                      now={progress}
                      variant="success"
                      style={{
                        height: '6px',
                        borderRadius: '4px',
                        background: '#e9edf2'
                      }}
                    />
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}

          {/* ===== STATISTIQUES ===== */}
          <Row className="g-3 mb-4">
            <Col md={4}>
              <div className="kpi-card kpi-primary">
                <div className="kpi-icon"><i className="ri-database-2-line fs-24"></i></div>
                <div className="kpi-content">
                  <span className="kpi-label">Total sauvegardes</span>
                  <h3 className="kpi-value">{stats.totalBackups}</h3>
                </div>
              </div>
            </Col>
            <Col md={4}>
              <div className="kpi-card" style={{ borderLeftColor: COLORS.info }}>
                <div className="kpi-icon" style={{ background: 'rgba(59,130,246,0.1)', color: COLORS.info }}>
                  <i className="ri-hard-drive-line fs-24"></i>
                </div>
                <div className="kpi-content">
                  <span className="kpi-label">Espace utilisé</span>
                  <h3 className="kpi-value" style={{ fontSize: '1.2rem' }}>{formatFileSize(stats.totalSize)}</h3>
                </div>
              </div>
            </Col>
            <Col md={4}>
              <div className="kpi-card" style={{ borderLeftColor: COLORS.success }}>
                <div className="kpi-icon" style={{ background: 'rgba(16,185,129,0.1)', color: COLORS.success }}>
                  <i className="ri-history-line fs-24"></i>
                </div>
                <div className="kpi-content">
                  <span className="kpi-label">Dernière sauvegarde</span>
                  <h3 className="kpi-value" style={{ fontSize: '1.1rem' }}>
                    {stats.lastBackup ? formatDate(stats.lastBackup) : 'Jamais'}
                  </h3>
                </div>
              </div>
            </Col>
          </Row>

          {/* ===== TABLEAU ===== */}
          <Row>
            <Col md={12}>
              <Card className="table-card-modern" style={{
                border: 'none',
                borderRadius: '0px',
                background: 'rgba(255,255,255,0.8)',
                backdropFilter: 'blur(12px)',
                boxShadow: '0 10px 30px rgba(0,0,0,0.05)',
                overflow: 'hidden'
              }}>
                <Card.Header style={{
                  background: 'rgba(255,255,255,0.5)',
                  borderBottom: '1px solid rgba(0,0,0,0.04)',
                  padding: '0.75rem 1.25rem'
                }}>
                  <div className="d-flex justify-content-between align-items-center">
                    <h6 className="fw-semibold mb-0" style={{ color: COLORS.primary, fontSize: '0.95rem' }}>
                      <i className="ri-list-check me-2" style={{ color: COLORS.primary }}></i>
                      Historique des sauvegardes
                    </h6>
                    <Badge bg="primary" className="rounded-pill px-3 py-2" style={{ fontSize: '0.7rem' }}>
                      {backups.length} fichier(s)
                    </Badge>
                  </div>
                </Card.Header>
                <Card.Body className="p-0">
                  <div className="table-responsive">
                    <Table hover className="mb-0 align-middle" style={{ fontSize: '0.85rem' }}>
                      <thead style={{ background: '#f8fafc' }}>
                        <tr>
                          <th className="px-3 py-2" style={{ color: '#475569', fontWeight: '600', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Fichier</th>
                          <th className="px-3 py-2" style={{ color: '#475569', fontWeight: '600', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Date</th>
                          <th className="px-3 py-2" style={{ color: '#475569', fontWeight: '600', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Taille</th>
                          <th className="px-3 py-2" style={{ color: '#475569', fontWeight: '600', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Type</th>
                          <th className="px-3 py-2 text-center" style={{ color: '#475569', fontWeight: '600', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {backups.length > 0 ? (
                          backups.map((backup, index) => (
                            <tr key={index} style={{ animation: `fadeIn 0.3s ease ${index * 0.05}s both` }}>
                              <td className="px-3 py-2">
                                <div className="d-flex align-items-center gap-2">
                                  <i className="ri-file-zip-line" style={{ color: COLORS.primary, fontSize: '1.1rem' }}></i>
                                  <code style={{
                                    background: '#f1f5f9',
                                    padding: '0.2rem 0.6rem',
                                    borderRadius: '6px',
                                    fontSize: '0.8rem',
                                    color: '#1e293b'
                                  }}>
                                    {backup.filename}
                                  </code>
                                </div>
                              </td>
                              <td className="px-3 py-2">
                                <div>
                                  <span>{formatDate(backup.date)}</span>
                                </div>
                              </td>
                              <td className="px-3 py-2">
                                <span className="badge rounded-pill" style={{
                                  background: '#f1f5f9',
                                  color: '#475569',
                                  padding: '0.3rem 0.8rem',
                                  fontSize: '0.7rem',
                                  fontWeight: '500'
                                }}>
                                  {formatFileSize(backup.size)}
                                </span>
                              </td>
                              <td className="px-3 py-2">
                                <span className="badge rounded-pill" style={{
                                  background: backup.type === 'manuel' ? COLORS.primary : COLORS.secondary,
                                  color: 'white',
                                  padding: '0.3rem 0.8rem',
                                  fontSize: '0.7rem',
                                  fontWeight: '500'
                                }}>
                                  {backup.type === 'manuel' ? 'Manuelle' : 'Automatique'}
                                </span>
                              </td>
                              <td className="px-3 py-2 text-center">
                                <div className="d-flex gap-1 justify-content-center">
                                  <button
                                    className="action-btn"
                                    style={{
                                      width: '32px',
                                      height: '32px',
                                      borderRadius: '8px',
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      background: 'rgba(16,185,129,0.12)',
                                      color: COLORS.success,
                                      border: 'none',
                                      transition: 'all 0.2s ease',
                                      cursor: 'pointer'
                                    }}
                                    onClick={() => handleDownload(backup.filename)}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.background = COLORS.success;
                                      e.currentTarget.style.color = 'white';
                                      e.currentTarget.style.transform = 'scale(1.1)';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.background = 'rgba(16,185,129,0.12)';
                                      e.currentTarget.style.color = COLORS.success;
                                      e.currentTarget.style.transform = 'scale(1)';
                                    }}
                                  >
                                    <i className="ri-download-line" style={{ fontSize: '0.9rem' }}></i>
                                  </button>
                                  <button
                                    className="action-btn"
                                    style={{
                                      width: '32px',
                                      height: '32px',
                                      borderRadius: '8px',
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      background: 'rgba(239,68,68,0.12)',
                                      color: COLORS.danger,
                                      border: 'none',
                                      transition: 'all 0.2s ease',
                                      cursor: 'pointer'
                                    }}
                                    onClick={() => handleDeleteClick(backup)}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.background = COLORS.danger;
                                      e.currentTarget.style.color = 'white';
                                      e.currentTarget.style.transform = 'scale(1.1)';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.background = 'rgba(239,68,68,0.12)';
                                      e.currentTarget.style.color = COLORS.danger;
                                      e.currentTarget.style.transform = 'scale(1)';
                                    }}
                                  >
                                    <i className="ri-delete-bin-line" style={{ fontSize: '0.9rem' }}></i>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="5" className="text-center py-5">
                              <div className="d-flex flex-column align-items-center">
                                <i className="ri-inbox-line mb-3" style={{ fontSize: '2.8rem', color: COLORS.primary, opacity: 0.25 }}></i>
                                <h6 className="fw-semibold mb-1" style={{ color: '#64748b' }}>Aucune sauvegarde trouvée</h6>
                                <p className="text-muted small mb-0">Commencez par créer votre première sauvegarde</p>
                              </div>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* ===== INFORMATION ===== */}
          <Row className="mt-4">
            <Col md={12}>
              <Card className="info-card-modern" style={{
                border: 'none',
                borderRadius: '0px',
                background: 'rgba(255,255,255,0.6)',
                backdropFilter: 'blur(8px)',
                boxShadow: '0 4px 12px rgba(0,0,0,0.03)'
              }}>
                <Card.Body className="p-3">
                  <div className="d-flex align-items-center gap-3">
                    <div style={{
                      width: '36px',
                      height: '36px',
                      borderRadius: '8px',
                      background: `${COLORS.info}20`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <i className="ri-information-line" style={{ color: COLORS.info, fontSize: '1.2rem' }}></i>
                    </div>
                    <div>
                      <p className="small text-muted mb-0">
                        <strong>Note :</strong> Les sauvegardes incluent toutes les données de l'application : membres, églises, abonnements, événements, finances et recommandations.
                      </p>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Footer />
        </div>
      </div>

      {/* ===== MODAL DE SUPPRESSION ===== */}
      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
        className="delete-modal-modern"
      >
        <Modal.Header
          closeButton
          className="border-0 pb-0"
          style={{
            background: `linear-gradient(135deg, ${COLORS.danger}, ${COLORS.rose})`,
            borderTopLeftRadius: '4px',
            borderTopRightRadius: '4px'
          }}
        >
          <Modal.Title className="w-100 text-center">
            <div className="delete-icon-wrapper mb-2">
              <div className="delete-icon-circle" style={{
                width: '70px',
                height: '70px',
                borderRadius: '4px',
                background: 'rgba(255,255,255,0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto'
              }}>
                <i className="ri-delete-bin-line" style={{ fontSize: '32px', color: 'white' }}></i>
              </div>
            </div>
            <h5 className="fw-bold text-white mb-0">Confirmer la suppression</h5>
            <p className="text-white-50 small mb-0">Cette action est irréversible</p>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="pt-4 px-4">
          {backupToDelete && (
            <>
              <p className="text-center text-muted mb-3">
                Êtes-vous sûr de vouloir supprimer définitivement cette sauvegarde ?
              </p>

              <div className="p-3 mb-3" style={{
                background: '#f8fafc',
                borderLeft: `3px solid ${COLORS.primary}`,
                borderRadius: '4px'
              }}>
                <div className="d-flex align-items-center gap-3">
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '8px',
                    background: `${COLORS.primary}20`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <i className="ri-file-zip-line" style={{ color: COLORS.primary, fontSize: '1.2rem' }}></i>
                  </div>
                  <div>
                    <div className="fw-bold" style={{ color: COLORS.primaryDark }}>
                      {backupToDelete.filename}
                    </div>
                    <div className="text-muted small">
                      <i className="ri-calendar-line me-1"></i>
                      {formatDate(backupToDelete.date)}
                    </div>
                    <span className="badge rounded-pill mt-1" style={{
                      background: backupToDelete.type === 'manuel' ? COLORS.primary : COLORS.secondary,
                      color: 'white',
                      fontSize: '0.65rem'
                    }}>
                      {backupToDelete.type === 'manuel' ? 'Manuelle' : 'Automatique'} • {formatFileSize(backupToDelete.size)}
                    </span>
                  </div>
                </div>
              </div>

              <div style={{
                background: `${COLORS.danger}10`,
                border: `1px solid ${COLORS.danger}30`,
                borderRadius: '4px',
                padding: '0.8rem 1rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.8rem'
              }}>
                <i className="ri-alert-line" style={{ color: COLORS.danger, fontSize: '1.3rem' }}></i>
                <span className="small text-muted">
                  Cette sauvegarde sera définitivement supprimée et ne pourra pas être récupérée.
                </span>
              </div>
            </>
          )}
        </Modal.Body>

        <Modal.Footer className="border-0 pt-0 pb-4 px-4 justify-content-center gap-2">
          <Button
            variant="light"
            onClick={() => setShowDeleteModal(false)}
            disabled={deleting}
            className="px-4"
            style={{
              borderRadius: '4px',
              padding: '0.5rem 1.5rem',
              border: '1px solid #d1d5db',
              color: '#4b5563',
              fontWeight: '500',
              fontSize: '0.85rem',
              transition: 'all 0.2s',
              background: 'white'
            }}
          >
            Annuler
          </Button>
          <Button
            variant="danger"
            onClick={handleDeleteConfirm}
            disabled={deleting}
            className="px-4"
            style={{
              background: `linear-gradient(135deg, ${COLORS.danger}, ${COLORS.rose})`,
              border: 'none',
              borderRadius: '4px',
              padding: '0.5rem 1.5rem',
              fontWeight: '500',
              fontSize: '0.85rem',
              boxShadow: '0 4px 10px rgba(239, 68, 68, 0.25)',
              transition: 'all 0.2s',
              color: 'white'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 8px 16px rgba(239, 68, 68, 0.35)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 10px rgba(239, 68, 68, 0.25)';
            }}
          >
            {deleting ? (
              <><Spinner size="sm" className="me-1" /> Suppression...</>
            ) : (
              <><i className="ri-delete-bin-line me-2"></i>Oui, supprimer</>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ===== STYLES ===== */}
      <style>{`
        .fade-in {
          animation: fadeIn 0.3s ease-out;
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .header-modern {
          background: white;
          padding: 1.2rem 1.8rem;
          box-shadow: 0 2px 8px rgba(0,0,0,0.04);
          border-left: 4px solid ${COLORS.secondary};
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
        }
        .header-left {
          display: flex;
          align-items: center;
          gap: 1rem;
        }
        .header-icon-wrapper {
          width: 48px;
          height: 48px;
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 1.4rem;
        }
        .gradient-text {
          color: #1e293b;
          font-size: 1.3rem;
          font-weight: 700;
          margin: 0;
        }
        .badge-modern {
          background: #f1f4f9;
          color: #475569;
          padding: 0.3rem 0.8rem;
          border-radius: 4px;
          font-size: 0.75rem;
          font-weight: 600;
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }
        .badge-modern i {
          color: ${COLORS.secondary};
        }
        .btn-modern-outline {
          border: 1px solid #d1d5db;
          background: white;
          padding: 0.4rem 1.2rem;
          border-radius: 4px;
          font-size: 0.85rem;
          font-weight: 500;
          color: #4b5563;
          transition: all 0.2s;
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }
        .btn-modern-outline:hover {
          border-color: ${COLORS.secondary};
          color: ${COLORS.secondary};
          background: #fafafa;
        }
        .btn-modern-primary {
          background: ${COLORS.primary};
          border: none;
          padding: 0.4rem 1.2rem;
          border-radius: 4px;
          font-size: 0.85rem;
          font-weight: 500;
          color: white;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          transition: all 0.2s;
        }
        .btn-modern-primary:hover {
          background: ${COLORS.primaryDark};
          box-shadow: 0 4px 10px rgba(41,90,167,0.3);
        }

        .kpi-card {
          background: white;
          border-radius: 0px;
          padding: 1rem;
          display: flex;
          align-items: center;
          gap: 1rem;
          box-shadow: 0 1px 3px rgba(0,0,0,0.05);
          transition: all 0.2s ease;
          height: 100%;
          border-left: 2px solid;
        }
        .kpi-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(0,0,0,0.08);
        }
        .kpi-primary { border-left-color: ${COLORS.primary}; }
        .kpi-icon {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .kpi-primary .kpi-icon { background: rgba(41, 90, 167, 0.1); color: ${COLORS.primary}; }
        .kpi-content { flex: 1; }
        .kpi-label {
          font-size: 0.7rem;
          text-transform: uppercase;
          color: #6c757d;
          font-weight: 600;
          letter-spacing: 0.5px;
        }
        .kpi-value {
          font-size: 1.3rem;
          font-weight: 700;
          margin: 0.25rem 0;
          color: #1a3c5e;
        }
        .fs-24 { font-size: 1.5rem; }

        .progress-card-modern {
          border: none;
          border-radius: 0px;
          background: rgba(255,255,255,0.8);
          backdropFilter: 'blur(8px)';
          box-shadow: 0 4px 12px rgba(0,0,0,0.03);
        }

        .table-card-modern {
          transition: all 0.3s ease;
        }
        .table-card-modern:hover {
          box-shadow: 0 16px 40px rgba(0,0,0,0.08) !important;
        }

        .info-card-modern {
          transition: all 0.2s ease;
        }
        .info-card-modern:hover {
          box-shadow: 0 8px 20px rgba(0,0,0,0.06) !important;
        }

        .action-btn {
          transition: all 0.2s ease;
        }

        .delete-modal-modern .modal-content {
          border: none;
          border-radius: 4px;
          overflow: hidden;
          box-shadow: 0 20px 40px rgba(0,0,0,0.15);
        }

        @media (max-width: 768px) {
          .header-modern {
            flex-direction: column;
            align-items: stretch;
            gap: 0.8rem;
            padding: 1rem;
          }
          .header-left {
            flex-wrap: wrap;
          }
          .kpi-card {
            flex-direction: column;
            text-align: center;
          }
        }
      `}</style>
    </>
  );
}